import { ReactElement } from 'react';

export type IconName =
  | 'overview'
  | 'modules'
  | 'applications'
  | 'roles'
  | 'users'
  | 'audit'
  | 'consents'
  | 'approvals';

/**
 * The eight navigation icons as inline SVG.
 *
 * No icon library for eight glyphs - it would outweigh what it delivers and
 * pull a dependency into a bundle that has none. They are drawn on one grid
 * with one stroke weight so the column reads as a set, and they inherit
 * currentColor so the active state needs no second colour.
 */
export function NavIcon({ name }: { name: IconName }) {
  return (
    <svg
      className="nav-icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

const PATHS: Record<IconName, ReactElement> = {
  // A stamp of approval: a tick inside the same frame.
  approvals: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 12.5l2.5 2.5L16 9.5" />
    </>
  ),
  // Four panes - the classic dashboard, and the only place it is used.
  overview: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  // Stacked layers: one module per connected API.
  modules: (
    <>
      <path d="M12 3 21 7.5 12 12 3 7.5z" />
      <path d="M3 12.5 12 17l9-4.5" />
      <path d="M3 17.5 12 22l9-4.5" />
    </>
  ),
  // A window - something that calls the gateway from outside.
  applications: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M3 9h18" />
      <path d="M7 6.5h.01M10 6.5h.01" />
    </>
  ),
  // A key: roles bundle what may be unlocked.
  roles: (
    <>
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h9" />
      <path d="M17 12v3.5" />
      <path d="M20.5 12v2.5" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
      <path d="M16.5 5.2a3.5 3.5 0 0 1 0 6.6" />
      <path d="M18.5 14.4c1.9.7 3 2.2 3 4.1" />
    </>
  ),
  // A sheet with lines - the record of what happened.
  audit: (
    <>
      <path d="M5.5 3h9L19 7.5V21H5.5z" />
      <path d="M14 3v5h5" />
      <path d="M9 12.5h6M9 16h6" />
    </>
  ),
  // A tick that was given: consent granted.
  consents: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.4 10.9 15 16 9.5" />
    </>
  ),
};
